/**
 * Dashboard API client.
 *
 * Same-origin, cookie-authenticated. The CSRF token is read from the
 * non-HttpOnly cookie the server sets alongside the session and echoed on
 * every mutating request — that pairing is what proves a request came from
 * this page rather than from another site that merely has the cookie sent
 * along with it.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function readCookie(name: string): string | undefined {
  return document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${name}=`))
    ?.slice(name.length + 1);
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);

  if (init.body) headers.set('Content-Type', 'application/json');
  if (!['GET', 'HEAD'].includes(method)) {
    const csrf = readCookie('x-csrf-token');
    if (csrf) headers.set('x-csrf-token', csrf);
  }

  const response = await fetch(path, { ...init, headers, credentials: 'same-origin' });

  if (response.status === 401) {
    // The session is gone. Send the person to sign in and bring them back to
    // exactly where they were, rather than dumping them on the inbox.
    const returnTo = encodeURIComponent(location.pathname + location.search);
    location.href = `/auth/login?returnTo=${returnTo}`;
    throw new ApiError('signed out', 401);
  }

  if (!response.ok) {
    const detail = (await response.json().catch(() => ({}))) as {
      message?: string;
      error?: string;
    };
    throw new ApiError(
      detail.message ?? `request failed (${response.status})`,
      response.status,
      detail.error,
    );
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/* ------------------------------------------------------------------ */

export interface Me {
  personId: string;
  displayName: string | null;
  email: string | null;
  grants: { appId: string | null; role: string }[];
  capabilities: {
    canTriage: boolean;
    canAssign: boolean;
    canManageApplications: boolean;
    canManageGrants: boolean;
  };
}

export interface ApplicationSummary {
  id: string;
  displayName: string;
  active: boolean;
  components: { key: string; name: string }[];
}

export interface Reporter {
  personId: string;
  displayName: string | null;
  email: string | null;
  department: string | null;
  jobTitle: string | null;
}

export interface FeedbackRow {
  id: string;
  publicRef: string;
  appId: string;
  appDisplayName: string;
  component: string | null;
  env: string;
  type: string;
  severity: string;
  priority: string | null;
  status: string;
  title: string;
  reporter: Reporter;
  assignee: Reporter | null;
  duplicateCount: number;
  attachmentCount: number;
  commentCount: number;
  githubIssueUrl: string | null;
  slaDueAt: string | null;
  slaBreachedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Summary {
  byStatus: Record<string, number>;
  bySeverity: Record<string, number>;
  slaBreached: number;
  unassigned: number;
}

export interface FeedbackFilters {
  appId?: string[];
  status?: string[];
  type?: string[];
  severity?: string[];
  search?: string;
  slaBreached?: boolean;
  sort?: string;
  cursor?: string;
}

export function buildQuery(filters: FeedbackFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      if (value.length > 0) params.set(key, value.join(','));
    } else if (typeof value === 'boolean') {
      if (value) params.set(key, 'true');
    } else if (String(value).length > 0) {
      params.set(key, String(value));
    }
  }
  return params.toString();
}

export const api = {
  me: () => request<Me>('/v1/dashboard/me'),
  applications: () => request<ApplicationSummary[]>('/v1/dashboard/applications'),
  summary: () => request<Summary>('/v1/dashboard/summary'),

  list: (filters: FeedbackFilters) =>
    request<{ items: FeedbackRow[]; nextCursor: string | null }>(
      `/v1/dashboard/feedback?${buildQuery(filters)}`,
    ),

  detail: (idOrRef: string) =>
    request<Record<string, unknown>>(`/v1/dashboard/feedback/${encodeURIComponent(idOrRef)}`),

  reporterHistory: (id: string) =>
    request<{ id: string; publicRef: string; appId: string; title: string; status: string }[]>(
      `/v1/dashboard/feedback/${encodeURIComponent(id)}/reporter-history`,
    ),

  update: (id: string, body: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/v1/dashboard/feedback/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  comment: (id: string, body: string, visibility: 'internal' | 'reporter_visible') =>
    request<Record<string, unknown>>(`/v1/dashboard/feedback/${encodeURIComponent(id)}/comments`, {
      method: 'POST',
      body: JSON.stringify({ body, visibility }),
    }),
};
